// src/components/LeagueSettings.js - PHASE 4: ADDED DYNASTY/DRAFT CONFIGURATION
import React, { useState, useEffect } from 'react';
import { 
  Crown, Settings, Trash2, AlertTriangle, Shield, Users, 
  Check, X, Eye, EyeOff, Lock, Unlock, Upload, Save, 
  AlertCircle, Trophy, DollarSign, Camera, Calendar,
  Globe, Copy, Share2
} from 'lucide-react';
import { leaguesAPI } from '../services/apiService';
import { dynastyTheme } from '../services/colorService';
import { parseIntOrNull, parseIntOrDefault, parseIntOrPrev } from '../services/formUtils';
import DeleteLeagueModal from './league-settings/DeleteLeagueModal';

const LeagueSettings = ({ leagueId, user, onLeagueDeleted }) => {
  const [league, setLeague] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [copiedInviteCode, setCopiedInviteCode] = useState(false);
  
  // New banner upload states
  const [uploadingBanner, setUploadingBanner] = useState(false);
  const [bannerPreview, setBannerPreview] = useState(null);

  // Banner customization states
  const [bannerConfig, setBannerConfig] = useState({
    height: 120,
    titleSize: 'text-3xl',
    titlePosition: 'left',
    titleStyle: 'font-bold'
  });
  
  // League settings state
  const [settings, setSettings] = useState({
    league_name: '',
    league_id: '',
    max_teams: 12,
    scoring_system: '',
    player_pool: '',
    league_status: '',
    created_date: '',
    league_banner_url: '',
    is_public: true,
    invite_code: '',
    
    // Scoring categories (from DB)
    scoring_categories: {
      hitting: ['R', 'HR', 'RBI', 'SB', 'AVG', 'OPS'],
      pitching: ['W', 'SV', 'ERA', 'WHIP', 'SO', 'QS']
    },
    
    // Financial settings
    use_salaries: false,
    min_salary: 1,
    
    // Position requirements
    position_requirements: {},
    bench_slots: 5,
    dl_slots: 0,
    minor_league_slots: 0,
    
    // Dynasty/Draft settings (Phase 4)
    league_type: 'dynasty',
    draft_rounds: 23,
    num_keepers: 15,
    dynasty_initial_draft_type: 'snake',
    dynasty_subsequent_draft_type: 'standings_lottery',
    lottery_enabled: false,
    lottery_weighting: 'nba_style',
    lottery_num_picks: 3,
    lottery_top_excluded: 3,
    include_minor_leagues: false,
    ml_activation_hitter_ab_threshold: 150,
    ml_activation_pitcher_ip_threshold: 50,
    ml_activation_window_days: 14
  });
  
  const [isCommissioner, setIsCommissioner] = useState(false);
  
  // League deletion state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteStep, setDeleteStep] = useState(1);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [finalConfirmation, setFinalConfirmation] = useState(false);

  // Helper function to format draft type labels
  const formatDraftType = (type) => {
    const typeMap = {
      'snake': 'Snake Draft',
      'random_repeating': 'Random (Same Order)',
      'basic_standings': 'Standings-Based',
      'standings_lottery': 'Standings with Lottery',
      'auction': 'Auction',
      'commissioner': 'Commissioner Sets Order'
    };
    return typeMap[type] || type;
  };

  useEffect(() => {
    loadLeagueDetails();
    loadLeagueSettings();
  }, [leagueId]);

  const loadLeagueDetails = async () => {
    try {
      setLoading(true);
      const response = await leaguesAPI.getLeagueDetails(leagueId);
      if (response.success) {
        setLeague(response.league);
        setIsCommissioner(response.league.role === 'commissioner');
        
        // Set basic settings from league object (including dynasty/draft fields if present)
        setSettings(prev => ({
          ...prev,
          league_name: response.league.league_name || '',
          league_id: leagueId,
          max_teams: response.league.league_settings?.max_teams ?? 12,
          scoring_system: response.league.league_settings?.scoring_system || 'rotisserie_ytd',
          player_pool: response.league.player_pool || 'american_national',
          league_status: response.league.league_status || 'setup',
          created_date: response.league.created_at ? new Date(response.league.created_at).toLocaleDateString() : '',
          use_salaries: response.league.salary_settings?.use_salaries || false,
          min_salary: response.league.salary_settings?.min_salary ?? 1,
          is_public: response.league.is_public !== undefined ? response.league.is_public : true,
          invite_code: response.league.invite_code || '',
          // Phase 4 fields
          league_type: response.league.league_type || 'dynasty',
          draft_rounds: response.league.draft_rounds ?? 23,
          num_keepers: response.league.num_keepers ?? 15,
          dynasty_initial_draft_type: response.league.dynasty_initial_draft_type || 'snake',
          dynasty_subsequent_draft_type: response.league.dynasty_subsequent_draft_type || 'standings_lottery',
          lottery_enabled: response.league.lottery_enabled || false,
          lottery_num_picks: response.league.lottery_num_picks ?? 3,
          lottery_top_excluded: response.league.lottery_top_excluded ?? 3
        }));
        
        // Load banner if exists
        if (response.league.league_banner_url) {
          setBannerPreview(response.league.league_banner_url);
          setSettings(prev => ({ ...prev, league_banner_url: response.league.league_banner_url }));
        }
      }
    } catch (error) {
      console.error('Error loading league details:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadLeagueSettings = async () => {
    try {
      // Get detailed settings from league_settings table
      const settingsResponse = await leaguesAPI.getLeagueSettings(leagueId);
      if (settingsResponse.success && settingsResponse.settings) {
        const dbSettings = settingsResponse.settings;
        
        if (dbSettings.scoring_categories) {
          const categories = dbSettings.scoring_categories;
            
          setSettings(prev => ({
            ...prev,
            scoring_categories: {
              hitting: categories.hitting || prev.scoring_categories.hitting,
              pitching: categories.pitching || prev.scoring_categories.pitching
            }
          }));
        }
        
        if (dbSettings.position_requirements) {
          const positions = dbSettings.position_requirements;
          
          setSettings(prev => ({
            ...prev,
            position_requirements: positions,
            bench_slots: parseIntOrDefault(dbSettings.bench_slots, 5),
            dl_slots: parseIntOrDefault(dbSettings.dl_slots, 0),
            minor_league_slots: parseIntOrDefault(dbSettings.minor_league_slots, 0)
          }));
        }
        
        // Update other settings including privacy and dynasty/draft settings (Phase 4)
        setSettings(prev => ({
          ...prev,
          max_teams: parseIntOrPrev(dbSettings.max_teams, prev.max_teams),
          league_banner_url: dbSettings.league_banner_url || prev.league_banner_url,
          is_public: dbSettings.is_public !== undefined ? dbSettings.is_public : prev.is_public,
          invite_code: dbSettings.invite_code || prev.invite_code,
          // Phase 4 dynasty/draft fields
          league_type: dbSettings.league_type || prev.league_type,
          draft_rounds: parseIntOrPrev(dbSettings.draft_rounds, prev.draft_rounds),
          num_keepers: parseIntOrPrev(dbSettings.num_keepers, prev.num_keepers),
          dynasty_initial_draft_type: dbSettings.dynasty_initial_draft_type || prev.dynasty_initial_draft_type,
          dynasty_subsequent_draft_type: dbSettings.dynasty_subsequent_draft_type || prev.dynasty_subsequent_draft_type,
          lottery_enabled: dbSettings.lottery_enabled !== undefined ? dbSettings.lottery_enabled : prev.lottery_enabled,
          lottery_num_picks: parseIntOrPrev(dbSettings.lottery_num_picks, prev.lottery_num_picks),
          lottery_top_excluded: parseIntOrPrev(dbSettings.lottery_top_excluded, prev.lottery_top_excluded),
          include_minor_leagues: dbSettings.include_minor_leagues !== undefined ? dbSettings.include_minor_leagues : prev.include_minor_leagues,
          ml_activation_hitter_ab_threshold: parseIntOrPrev(dbSettings.ml_activation_hitter_ab_threshold, prev.ml_activation_hitter_ab_threshold),
          ml_activation_pitcher_ip_threshold: parseIntOrPrev(dbSettings.ml_activation_pitcher_ip_threshold, prev.ml_activation_pitcher_ip_threshold),
          ml_activation_window_days: parseIntOrPrev(dbSettings.ml_activation_window_days, prev.ml_activation_window_days)
        }));
        
        if (dbSettings.league_banner_url) {
          setBannerPreview(dbSettings.league_banner_url);
        }

        // Load banner config if exists
        if (dbSettings.banner_config) {
          const config = dbSettings.banner_config;
          setBannerConfig({
            height: config.height ?? 120,
            titleSize: config.titleSize || 'text-3xl',
            titlePosition: config.titlePosition || 'left',
            titleStyle: config.titleStyle || 'font-bold'
          });
        }
      }
    } catch (error) {
      console.log('Could not fetch league settings:', error);
    }
  };

  const copyInviteCode = () => {
    if (settings.invite_code) {
      navigator.clipboard.writeText(settings.invite_code);
      setCopiedInviteCode(true);
      setMessage({ text: 'Invite code copied to clipboard!', type: 'success' });
      setTimeout(() => {
        setCopiedInviteCode(false);
        setMessage({ text: '', type: '' });
      }, 3000);
    }
  };

  const handleBannerUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check permissions
    if (!isCommissioner) {
      setMessage({ text: 'Only commissioners can upload league banners', type: 'error' });
      return;
    }

    // Validate file
    if (!file.type.startsWith('image/')) {
      setMessage({ text: 'Please select an image file', type: 'error' });
      return;
    }

    if (file.size > 50 * 1024 * 1024) { // 50MB limit for panoramic banners
      setMessage({ text: 'Image must be less than 50MB', type: 'error' });
      return;
    }

    try {
      setUploadingBanner(true);
      setMessage({ text: 'Uploading league banner...', type: 'info' });

      // Use the new uploadLeagueBanner function from apiService
      const result = await leaguesAPI.uploadLeagueBanner(leagueId, file);
      
      if (result.success) {
        const bannerUrl = result.banner_url;
        
        // Update preview and settings
        setBannerPreview(bannerUrl);
        setSettings(prev => ({
          ...prev,
          league_banner_url: bannerUrl
        }));

        // Save to database
        await saveLeagueBanner(bannerUrl);
        
        setMessage({ text: 'League banner uploaded successfully!', type: 'success' });
      } else {
        throw new Error('Upload failed');
      }

    } catch (error) {
      console.error('Banner upload error:', error);
      setMessage({ text: error.message || 'Failed to upload banner', type: 'error' });
    } finally {
      setUploadingBanner(false);
    }
  };

  const saveLeagueBanner = async (bannerUrl) => {
    try {
      await leaguesAPI.updateLeagueSettings(leagueId, {
        league_banner_url: bannerUrl
      });
    } catch (error) {
      console.error('Failed to save banner URL:', error);
    }
  };

  const handleMaxTeamsChange = (value) => {
    // Check permissions
    if (!isCommissioner) {
      setMessage({ text: 'Only commissioners can change settings', type: 'error' });
      return;
    }

    // Only allow changes if not in draft or active status
    if (['drafting', 'active'].includes(settings.league_status)) {
      setMessage({ text: 'Cannot change max teams after draft has started', type: 'error' });
      return;
    }
    
    const newValue = parseIntOrPrev(value, settings.max_teams);
    if (newValue < 4 || newValue > 20) {
      setMessage({ text: 'Max teams must be between 4 and 20', type: 'error' });
      return;
    }
    
    setSettings(prev => ({ ...prev, max_teams: newValue }));
  };

  const saveSettings = async () => {
    if (!isCommissioner) {
      setMessage({ text: 'Only commissioners can save settings', type: 'error' });
      return;
    }

    // Required-field validation: parseIntOrNull onChange writes null on
    // empty input. Reject save before bounds checks so null doesn't fall
    // through `null < 1 || > N` (where Number(null) === 0 makes the
    // lower-bound check spuriously fire on cleared fields, hiding the
    // true "required" semantic with a misleading bounds error).
    //
    // Gates match each field's JSX render condition. Without these gates,
    // save would reject on a null-but-invisible field after the user
    // toggled the gating condition mid-session — a UX dead-end.
    if (settings.league_type === 'dynasty' && settings.num_keepers === null) {
      setMessage({ text: 'Number of keepers is required', type: 'error' });
      return;
    }
    if (settings.lottery_enabled && settings.lottery_top_excluded === null) {
      setMessage({ text: 'Lottery top excluded is required', type: 'error' });
      return;
    }
    if (settings.draft_rounds === null) {
      setMessage({ text: 'Draft rounds is required', type: 'error' });
      return;
    }
    if (settings.lottery_enabled && settings.lottery_num_picks === null) {
      setMessage({ text: 'Lottery picks is required when lottery is enabled', type: 'error' });
      return;
    }

    // Validation for Phase 4 fields (runs after null checks so bounds
    // operate on valid integers).
    if (settings.draft_rounds < 1 || settings.draft_rounds > 40) {
      setMessage({ text: 'Draft rounds must be between 1 and 40', type: 'error' });
      return;
    }


    // No validation needed for num_keepers against draft_rounds
    // Dynasty leagues are flexible:
    // - Teams can keep different amounts (one team 15, another 10)
    // - Teams trade picks (some don't draft until round 6+)
    // - Draft continues until all rosters are full
    // num_keepers is simply the MAXIMUM allowed, not a requirement

    if (settings.lottery_enabled) {
      const maxLotteryPicks = settings.max_teams - (settings.lottery_top_excluded || 0);
      if (settings.lottery_num_picks < 1 || settings.lottery_num_picks > maxLotteryPicks) {
        setMessage({ text: `Lottery picks must be between 1 and ${maxLotteryPicks}`, type: 'error' });
        return;
      }
      if (settings.lottery_top_excluded < 0 || settings.lottery_top_excluded > 10) {
        setMessage({ text: 'Top teams excluded must be between 0 and 10', type: 'error' });
        return;
      }
    }

    try {
      setSaving(true);
      
      const updateData = {
        max_teams: settings.max_teams,
        league_name: settings.league_name,
        league_banner_url: settings.league_banner_url,
        banner_config: bannerConfig, // Add banner config
        // Phase 4 fields
        league_type: settings.league_type,
        draft_rounds: settings.draft_rounds,
        num_keepers: settings.num_keepers,
        dynasty_initial_draft_type: settings.dynasty_initial_draft_type,
        dynasty_subsequent_draft_type: settings.dynasty_subsequent_draft_type,
        lottery_enabled: settings.lottery_enabled,
        lottery_num_picks: settings.lottery_num_picks,
        lottery_top_excluded: settings.lottery_top_excluded,
        include_minor_leagues: settings.include_minor_leagues,
        ml_activation_hitter_ab_threshold: settings.ml_activation_hitter_ab_threshold,
        ml_activation_pitcher_ip_threshold: settings.ml_activation_pitcher_ip_threshold,
        ml_activation_window_days: settings.ml_activation_window_days
      };

      const response = await leaguesAPI.updateLeagueSettings(leagueId, updateData);
      
      if (response.success) {
        setMessage({ text: 'Settings saved successfully!', type: 'success' });
        // Update the league object
        if (league) {
          setLeague({ ...league, max_teams: settings.max_teams });
        }
      } else {
        throw new Error(response.error || 'Failed to save settings');
      }
    } catch (error) {
      console.error('Save settings error:', error);
      setMessage({ text: 'Failed to save settings', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteLeague = async () => {
    if (!isCommissioner) {
      setMessage({ text: 'Only commissioners can delete leagues', type: 'error' });
      return;
    }

    try {
      setSaving(true);
      
      const response = await leaguesAPI.deleteLeague(leagueId);
      
      if (response.success) {
        if (onLeagueDeleted) {
          onLeagueDeleted(league.league_name);
        }
      } else {
        throw new Error(response.message || 'Failed to delete league');
      }
      
    } catch (error) {
      console.error('Error deleting league:', error);
      alert('Failed to delete league. Please try again.');
    } finally {
      setSaving(false);
      setShowDeleteModal(false);
      setDeleteStep(1);
      setDeleteConfirmText('');
      setFinalConfirmation(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="flex items-center space-x-3">
          <div className={`w-8 h-8 border-2 border-t-transparent animate-spin rounded-full ${dynastyTheme.classes.border.primary}`} />
          <span className={`${dynastyTheme.classes.text.white} text-lg`}>Loading league settings...</span>
        </div>
      </div>
    );
  }

  // Non-commissioners get read-only view
  if (!isCommissioner) {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className={`${dynastyTheme.components.card.base} p-6`}>
          <div className="flex items-center space-x-3 mb-2">
            <Lock className={`w-8 h-8 ${dynastyTheme.classes.text.neutral}`} />
            <h1 className={dynastyTheme.components.heading.h1}>League Settings</h1>
          </div>
          <p className={dynastyTheme.classes.text.error}>
            View Only - Commissioner access required to edit settings
          </p>
        </div>

        {/* Private League Invite Code Display (visible to all members) */}
        {!settings.is_public && settings.invite_code && (
          <div className={`${dynastyTheme.components.card.base} p-6`}>
            <div className="flex items-center space-x-3 mb-4">
              <Lock className={`w-6 h-6 ${dynastyTheme.classes.text.primary}`} />
              <h3 className={`text-xl font-bold ${dynastyTheme.classes.text.white}`}>Private League</h3>
            </div>
            
            <div className={`p-4 rounded-lg ${dynastyTheme.classes.bg.darkLighter} border ${dynastyTheme.classes.border.primary}`}>
              <p className={`text-sm ${dynastyTheme.classes.text.neutralLight} mb-3`}>
                Share this invite code with players you want to join:
              </p>
              <div className="flex items-center gap-3">
                <code className={`text-2xl font-bold ${dynastyTheme.classes.text.primary} tracking-wider`}>
                  {settings.invite_code}
                </code>
                <button
                  onClick={copyInviteCode}
                  className={`${dynastyTheme.utils.getComponent('button', 'secondary', 'sm')} flex items-center gap-2`}
                >
                  {copiedInviteCode ? (
                    <>
                      <Check className="w-4 h-4" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4" />
                      Copy
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Show read-only settings */}
        <div className={`${dynastyTheme.components.card.base} p-6`}>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className={dynastyTheme.components.label}>League Name</label>
                <div className={`p-3 rounded-lg ${dynastyTheme.classes.bg.darkLighter} ${dynastyTheme.classes.text.neutralLight}`}>
                  {settings.league_name || 'N/A'}
                </div>
              </div>
              <div>
                <label className={dynastyTheme.components.label}>League ID</label>
                <div className={`p-3 rounded-lg ${dynastyTheme.classes.bg.darkLighter} ${dynastyTheme.classes.text.neutralLight}`}>
                  {settings.league_id || 'N/A'}
                </div>
              </div>
              <div>
                <label className={dynastyTheme.components.label}>Maximum Teams</label>
                <div className={`p-3 rounded-lg ${dynastyTheme.classes.bg.darkLighter} ${dynastyTheme.classes.text.neutralLight}`}>
                  {settings.max_teams}
                </div>
              </div>
              <div>
                <label className={dynastyTheme.components.label}>League Status</label>
                <div className={`p-3 rounded-lg ${dynastyTheme.classes.bg.darkLighter} ${dynastyTheme.classes.text.neutralLight}`}>
                  {settings.league_status?.replace(/_/g, ' ') || 'N/A'}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Commissioner view - full edit access
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className={`${dynastyTheme.components.card.base} p-6`}>
        <div className="flex items-center space-x-3 mb-2">
          <Crown className={`w-8 h-8 ${dynastyTheme.classes.text.primary}`} />
          <h1 className={dynastyTheme.components.heading.h1}>League Settings</h1>
        </div>
        <p className={dynastyTheme.classes.text.neutralLight}>
          Manage your league configuration and settings.
        </p>
      </div>

      {/* Message Display */}
      {message.text && (
        <div className={`p-4 rounded-lg border flex items-center gap-2 ${
          message.type === 'error' 
            ? 'bg-red-500/20 text-red-400 border-red-500/30'
            : message.type === 'info'
            ? 'bg-blue-500/20 text-blue-400 border-blue-500/30'
            : 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30'
        }`}>
          <AlertCircle className="w-5 h-5" />
          <span>{message.text}</span>
        </div>
      )}

      {/* Privacy Settings & Invite Code */}
      <div className={`${dynastyTheme.components.card.base} p-6`}>
        <div className="flex items-center space-x-3 mb-4">
          {settings.is_public ? (
            <>
              <Globe className={`w-6 h-6 ${dynastyTheme.classes.text.primary}`} />
              <h3 className={`text-xl font-bold ${dynastyTheme.classes.text.white}`}>Public League</h3>
            </>
          ) : (
            <>
              <Lock className={`w-6 h-6 ${dynastyTheme.classes.text.primary}`} />
              <h3 className={`text-xl font-bold ${dynastyTheme.classes.text.white}`}>Private League</h3>
            </>
          )}
        </div>
        
        {settings.is_public ? (
          <div className={`p-4 rounded-lg ${dynastyTheme.classes.bg.darkLighter}`}>
            <p className={`text-sm ${dynastyTheme.classes.text.white} mb-2`}>
              This league is visible in public league discovery
            </p>
            <p className={`text-xs ${dynastyTheme.classes.text.neutralLight}`}>
              Anyone can find and join this league through the browse leagues feature.
            </p>
          </div>
        ) : (
          <div className={`p-4 rounded-lg ${dynastyTheme.classes.bg.darkLighter} border ${dynastyTheme.classes.border.primary}`}>
            <p className={`text-sm ${dynastyTheme.classes.text.white} mb-3`}>
              Share this invite code with players you want to join:
            </p>
            <div className="flex items-center gap-3">
              <code className={`text-2xl font-bold ${dynastyTheme.classes.text.primary} tracking-wider`}>
                {settings.invite_code || 'GENERATING...'}
              </code>
              {settings.invite_code && (
                <button
                  onClick={copyInviteCode}
                  className={`${dynastyTheme.utils.getComponent('button', 'secondary', 'sm')} flex items-center gap-2`}
                >
                  {copiedInviteCode ? (
                    <>
                      <Check className="w-4 h-4" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4" />
                      Copy
                    </>
                  )}
                </button>
              )}
            </div>
            <p className={`text-xs ${dynastyTheme.classes.text.neutralLight} mt-3`}>
              Only people with this code can join your league.
            </p>
          </div>
        )}
      </div>

      {/* League Banner Upload */}
      <div className={`${dynastyTheme.components.card.base} p-6`}>
        <div className="flex items-center space-x-3 mb-4">
          <Camera className={`w-6 h-6 ${dynastyTheme.classes.text.primary}`} />
          <h3 className={`text-xl font-bold ${dynastyTheme.classes.text.white}`}>League Banner</h3>
        </div>
        
        {/* Banner Preview */}
        {bannerPreview && (
          <div className="mb-4 relative">
            <img 
              src={bannerPreview} 
              alt="League Banner"
              className="w-full h-48 object-cover rounded-lg"
            />
            <button
              onClick={() => {
                setBannerPreview(null);
                setSettings(prev => ({ ...prev, league_banner_url: '' }));
              }}
              className={`absolute top-2 right-2 p-2 rounded-full ${dynastyTheme.classes.bg.error} ${dynastyTheme.classes.text.white}`}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}
        
        <div className="flex items-center gap-4">
          <label className={`${dynastyTheme.utils.getComponent('button', 'secondary', 'sm')} ${
            uploadingBanner ? 'opacity-50 cursor-not-allowed' : ''
          }`}>
            <input
              type="file"
              accept="image/*"
              onChange={handleBannerUpload}
              className="hidden"
              disabled={uploadingBanner}
            />
            <Upload className="inline w-4 h-4 mr-2" />
            {uploadingBanner ? 'Uploading...' : 'Upload Banner'}
          </label>
        </div>
        <p className={`mt-2 text-xs ${dynastyTheme.classes.text.neutralLight}`}>
          Recommended: 1920x400px panoramic, max 50MB. This banner will appear on your League Home page.
        </p>

        {/* Banner Customization Controls */}
        <div className="mt-6 pt-6 border-t border-neutral-700">
          <h4 className={`text-lg font-semibold ${dynastyTheme.classes.text.white} mb-4`}>
            Banner Display Settings
          </h4>
          
          <div className="space-y-4">
            {/* Banner Height */}
            <div>
              <label className={dynastyTheme.components.label}>
                Banner Height
              </label>
              <div className="flex items-center gap-4">
                <input
                  type="range"
                  min="100"
                  max="200"
                  step="10"
                  value={bannerConfig.height}
                  onChange={(e) => setBannerConfig(prev => ({ ...prev, height: parseIntOrDefault(e.target.value, 120) }))}
                  className="flex-1"
                />
                <span className={`text-sm ${dynastyTheme.classes.text.primary} font-mono w-16 text-right`}>
                  {bannerConfig.height}px
                </span>
              </div>
              <p className={`mt-1 text-xs ${dynastyTheme.classes.text.neutralLight}`}>
                Adjust the height of the banner across all league pages
              </p>
            </div>

            {/* Title Font Size */}
            <div>
              <label className={dynastyTheme.components.label}>
                Title Font Size
              </label>
              <select
                value={bannerConfig.titleSize}
                onChange={(e) => setBannerConfig(prev => ({ ...prev, titleSize: e.target.value }))}
                className={dynastyTheme.components.select}
              >
                <option value="text-xl">Small (XL)</option>
                <option value="text-2xl">Medium (2XL)</option>
                <option value="text-3xl">Large (3XL) - Default</option>
                <option value="text-4xl">Extra Large (4XL)</option>
                <option value="text-5xl">Huge (5XL)</option>
              </select>
            </div>

            {/* Title Position */}
            <div>
              <label className={dynastyTheme.components.label}>
                Title Position
              </label>
              <select
                value={bannerConfig.titlePosition}
                onChange={(e) => setBannerConfig(prev => ({ ...prev, titlePosition: e.target.value }))}
                className={dynastyTheme.components.select}
              >
                <option value="left">Left Aligned</option>
                <option value="center">Center Aligned</option>
                <option value="right">Right Aligned</option>
              </select>
            </div>

            {/* Title Font Style */}
            <div>
              <label className={dynastyTheme.components.label}>
                Title Font Weight
              </label>
              <select
                value={bannerConfig.titleStyle}
                onChange={(e) => setBannerConfig(prev => ({ ...prev, titleStyle: e.target.value }))}
                className={dynastyTheme.components.select}
              >
                <option value="font-normal">Normal</option>
                <option value="font-semibold">Semi Bold</option>
                <option value="font-bold">Bold - Default</option>
                <option value="font-extrabold">Extra Bold</option>
              </select>
            </div>

            {/* Preview */}
            {bannerPreview && (
              <div>
                <label className={dynastyTheme.components.label}>
                  Preview
                </label>
                <div 
                  className="relative overflow-hidden rounded-lg border border-neutral-700"
                  style={{ height: `${bannerConfig.height}px` }}
                >
                  <img
                    src={bannerPreview}
                    alt="Banner Preview"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-b from-neutral-900/20 via-transparent to-neutral-900/70"></div>
                  <div className="absolute inset-0 flex items-center px-6">
                    <h1
                      className={`${bannerConfig.titleSize} ${bannerConfig.titleStyle} ${dynastyTheme.classes.text.white} w-full`}
                      style={{
                        textAlign: bannerConfig.titlePosition,
                        textShadow: '2px 2px 8px rgba(0,0,0,0.9), 0 0 20px rgba(0,0,0,0.7)'
                      }}
                    >
                      {settings.league_name || 'Your League Name'}
                    </h1>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* League Information */}
      <div className={`${dynastyTheme.components.card.base} p-6`}>
        <div className="flex items-center space-x-3 mb-4">
          <Settings className={`w-6 h-6 ${dynastyTheme.classes.text.primary}`} />
          <h3 className={`text-xl font-bold ${dynastyTheme.classes.text.white}`}>League Information</h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className={dynastyTheme.components.label}>
              League Name
            </label>
            <input
              type="text"
              value={settings.league_name || ''}
              onChange={(e) => setSettings(prev => ({ ...prev, league_name: e.target.value }))}
              className={`${dynastyTheme.components.input} w-full`}
            />
          </div>
          
          <div>
            <label className={dynastyTheme.components.label}>
              League ID
            </label>
            <input
              type="text"
              value={settings.league_id || ''}
              className={`${dynastyTheme.components.input} w-full opacity-50`}
              readOnly
            />
          </div>
          
          <div>
            <label className={dynastyTheme.components.label}>
              Scoring System
            </label>
            <input
              type="text"
              value={settings.scoring_system?.replace(/_/g, ' ') || ''}
              className={`${dynastyTheme.components.input} w-full opacity-50`}
              readOnly
            />
          </div>
          
          <div>
            <label className={dynastyTheme.components.label}>
              Player Pool
            </label>
            <input
              type="text"
              value={settings.player_pool?.replace(/_/g, ' ') || ''}
              className={`${dynastyTheme.components.input} w-full opacity-50`}
              readOnly
            />
          </div>
          
          <div>
            <label className={dynastyTheme.components.label}>
              Maximum Teams
              {['drafting', 'active'].includes(settings.league_status) && (
                <span className="ml-2 text-xs text-amber-400">(Locked)</span>
              )}
            </label>
            <input
              type="number"
              value={settings.max_teams ?? ''}
              onChange={(e) => handleMaxTeamsChange(e.target.value)}
              className={`${dynastyTheme.components.input} w-full`}
              min="4"
              max="20"
              disabled={['drafting', 'active'].includes(settings.league_status)}
            />
          </div>
          
          <div>
            <label className={dynastyTheme.components.label}>
              Created Date
            </label>
            <input
              type="text"
              value={settings.created_date || ''}
              className={`${dynastyTheme.components.input} w-full opacity-50`}
              readOnly
            />
          </div>
        </div>
      </div>

      {/* ====================================================================== */}
      {/* PHASE 4: DRAFT & DYNASTY CONFIGURATION */}
      {/* ====================================================================== */}
      <div className={`${dynastyTheme.components.card.base} p-6`}>
        <div className="flex items-center space-x-3 mb-4">
          <Calendar className={`w-6 h-6 ${dynastyTheme.classes.text.primary}`} />
          <h3 className={`text-xl font-bold ${dynastyTheme.classes.text.white}`}>Draft & Dynasty Configuration</h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* League Type */}
          <div>
            <label className={dynastyTheme.components.label}>
              League Type
            </label>
            <select
              value={settings.league_type || 'dynasty'}
              onChange={(e) => setSettings(prev => ({ ...prev, league_type: e.target.value }))}
              className={`${dynastyTheme.components.input} w-full`}
            >
              <option value="dynasty">Dynasty (Keeper League)</option>
              <option value="redraft">Redraft (Fresh Start Each Season)</option>
            </select>
            <p className={`text-xs mt-1 ${dynastyTheme.classes.text.neutralLight}`}>
              Can be changed mid-season if your league wants to keep going
            </p>
          </div>

          {/* Draft Rounds */}
          <div>
            <label className={dynastyTheme.components.label}>
              Draft Rounds
            </label>
            <input
              type="number"
              value={settings.draft_rounds ?? ''}
              onChange={(e) => setSettings(prev => ({ ...prev, draft_rounds: parseIntOrNull(e.target.value) }))}
              className={`${dynastyTheme.components.input} w-full`}
              min="1"
              max="40"
            />
            <p className={`text-xs mt-1 ${dynastyTheme.classes.text.neutralLight}`}>
              Draft continues until rosters are full (varies by keepers/trades)
            </p>
          </div>

          {/* Number of Keepers (Dynasty Only) */}
          {settings.league_type === 'dynasty' && (
            <div>
              <label className={dynastyTheme.components.label}>
                Maximum Keepers Per Team
              </label>
              <input
                type="number"
                value={settings.num_keepers ?? ''}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === '') {
                    setSettings(prev => ({ ...prev, num_keepers: null }));
                  } else {
                    const parsed = parseInt(v, 10);
                    if (!isNaN(parsed)) {
                      setSettings(prev => ({ ...prev, num_keepers: parsed }));
                    }
                  }
                }}
                className={`${dynastyTheme.components.input} w-full`}
                min="0"
              />
              <p className={`text-xs mt-1 ${dynastyTheme.classes.text.neutralLight}`}>
                Maximum players each team can keep (teams may keep fewer)
              </p>
            </div>
          )}

          {/* Initial Draft Type (Dynasty Only) */}
          {settings.league_type === 'dynasty' && (
            <div>
              <label className={dynastyTheme.components.label}>
                Initial Draft Type
              </label>
              <select
                value={settings.dynasty_initial_draft_type || 'snake'}
                onChange={(e) => setSettings(prev => ({ ...prev, dynasty_initial_draft_type: e.target.value }))}
                className={`${dynastyTheme.components.input} w-full`}
              >
                <option value="snake">Snake Draft</option>
                <option value="random_repeating">Random (Same Order)</option>
                <option value="commissioner">Commissioner Sets Order</option>
              </select>
              <p className={`text-xs mt-1 ${dynastyTheme.classes.text.neutralLight}`}>
                First season draft algorithm
              </p>
            </div>
          )}

          {/* Subsequent Draft Type (Dynasty Only) */}
          {settings.league_type === 'dynasty' && (
            <div>
              <label className={dynastyTheme.components.label}>
                Subsequent Draft Type
              </label>
              <select
                value={settings.dynasty_subsequent_draft_type || 'standings_lottery'}
                onChange={(e) => setSettings(prev => ({ ...prev, dynasty_subsequent_draft_type: e.target.value }))}
                className={`${dynastyTheme.components.input} w-full`}
              >
                <option value="basic_standings">Standings-Based (Worst Picks First)</option>
                <option value="standings_lottery">Standings with Lottery</option>
                <option value="auction">Auction</option>
                <option value="commissioner">Commissioner Sets Order</option>
              </select>
              <p className={`text-xs mt-1 ${dynastyTheme.classes.text.neutralLight}`}>
                Draft algorithm for year 2 and beyond
              </p>
            </div>
          )}

          {/* Minor Leagues Toggle */}
          <div>
            <label className={dynastyTheme.components.label}>
              Minor Leagues
            </label>
            <select
              value={settings.include_minor_leagues ? 'enabled' : 'disabled'}
              onChange={(e) => setSettings(prev => ({ ...prev, include_minor_leagues: e.target.value === 'enabled' }))}
              className={`${dynastyTheme.components.input} w-full`}
            >
              <option value="disabled">Disabled</option>
              <option value="enabled">Enabled</option>
            </select>
            <p className={`text-xs mt-1 ${dynastyTheme.classes.text.neutralLight}`}>
              Enable minor league draft and phantom player system
            </p>
          </div>

          {/* Minor League Activation Settings (only when minor leagues enabled) */}
          {settings.include_minor_leagues && (
            <>
              <div>
                <label className={dynastyTheme.components.label}>
                  Hitter AB Threshold
                </label>
                <input
                  type="number"
                  value={settings.ml_activation_hitter_ab_threshold}
                  onChange={(e) => setSettings(prev => ({ ...prev, ml_activation_hitter_ab_threshold: parseIntOrDefault(e.target.value, 150) }))}
                  className={`${dynastyTheme.components.input} w-full`}
                  min="1"
                />
                <p className={`text-xs mt-1 ${dynastyTheme.classes.text.neutralLight}`}>
                  At-bats before forced activation (default: 150)
                </p>
              </div>
              <div>
                <label className={dynastyTheme.components.label}>
                  Pitcher IP Threshold
                </label>
                <input
                  type="number"
                  value={settings.ml_activation_pitcher_ip_threshold}
                  onChange={(e) => setSettings(prev => ({ ...prev, ml_activation_pitcher_ip_threshold: parseIntOrDefault(e.target.value, 50) }))}
                  className={`${dynastyTheme.components.input} w-full`}
                  min="1"
                />
                <p className={`text-xs mt-1 ${dynastyTheme.classes.text.neutralLight}`}>
                  Innings pitched before forced activation (default: 50)
                </p>
              </div>
              <div>
                <label className={dynastyTheme.components.label}>
                  Activation Window (days)
                </label>
                <input
                  type="number"
                  value={settings.ml_activation_window_days}
                  onChange={(e) => setSettings(prev => ({ ...prev, ml_activation_window_days: parseIntOrDefault(e.target.value, 14) }))}
                  className={`${dynastyTheme.components.input} w-full`}
                  min="1"
                  max="60"
                />
                <p className={`text-xs mt-1 ${dynastyTheme.classes.text.neutralLight}`}>
                  Days to activate after hitting threshold (default: 14)
                </p>
              </div>
            </>
          )}

          {/* Lottery Enabled Toggle */}
          <div>
            <label className={dynastyTheme.components.label}>
              Lottery System
            </label>
            <select
              value={settings.lottery_enabled ? 'enabled' : 'disabled'}
              onChange={(e) => setSettings(prev => ({ ...prev, lottery_enabled: e.target.value === 'enabled' }))}
              className={`${dynastyTheme.components.input} w-full`}
            >
              <option value="disabled">Disabled</option>
              <option value="enabled">Enabled</option>
            </select>
            <p className={`text-xs mt-1 ${dynastyTheme.classes.text.neutralLight}`}>
              NBA-style lottery for draft order
            </p>
          </div>

          {/* Lottery Settings (if enabled) */}
          {settings.lottery_enabled && (
            <>
              <div>
                <label className={dynastyTheme.components.label}>
                  Lottery Picks
                </label>
                <input
                  type="number"
                  value={settings.lottery_num_picks ?? ''}
                  onChange={(e) => setSettings(prev => ({ ...prev, lottery_num_picks: parseIntOrNull(e.target.value) }))}
                  className={`${dynastyTheme.components.input} w-full`}
                  min="1"
                  max="5"
                />
                <p className={`text-xs mt-1 ${dynastyTheme.classes.text.neutralLight}`}>
                  How many top picks are determined by lottery (1-5)
                </p>
              </div>

              <div>
                <label className={dynastyTheme.components.label}>
                  Top Teams Excluded
                </label>
                <input
                  type="number"
                  value={settings.lottery_top_excluded ?? ''}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === '') {
                      setSettings(prev => ({ ...prev, lottery_top_excluded: null }));
                    } else {
                      const parsed = parseInt(v, 10);
                      if (!isNaN(parsed)) {
                        setSettings(prev => ({ ...prev, lottery_top_excluded: parsed }));
                      }
                    }
                  }}
                  className={`${dynastyTheme.components.input} w-full`}
                  min="0"
                  max="10"
                />
                <p className={`text-xs mt-1 ${dynastyTheme.classes.text.neutralLight}`}>
                  Top finishers NOT eligible for lottery (0-10)
                </p>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Scoring Categories Display */}
      <div className={`${dynastyTheme.components.card.base} p-6`}>
        <div className="flex items-center space-x-3 mb-4">
          <Trophy className={`w-6 h-6 ${dynastyTheme.classes.text.primary}`} />
          <h3 className={`text-xl font-bold ${dynastyTheme.classes.text.white}`}>Scoring Categories</h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h4 className={`font-semibold mb-2 ${dynastyTheme.classes.text.white}`}>
              Hitting Categories
            </h4>
            <div className="flex flex-wrap gap-2">
              {settings.scoring_categories.hitting.map(cat => (
                <span key={cat} className={dynastyTheme.components.badge.neutral}>
                  {cat}
                </span>
              ))}
            </div>
          </div>
          
          <div>
            <h4 className={`font-semibold mb-2 ${dynastyTheme.classes.text.white}`}>
              Pitching Categories
            </h4>
            <div className="flex flex-wrap gap-2">
              {settings.scoring_categories.pitching.map(cat => (
                <span key={cat} className={dynastyTheme.components.badge.neutral}>
                  {cat}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <button
          onClick={saveSettings}
          disabled={saving}
          className={dynastyTheme.utils.getComponent('button', 'primary', 'md')}
        >
          <Save className="inline w-5 h-5 mr-2" />
          {saving ? 'Saving...' : 'Save Settings'}
        </button>
      </div>

      {/* Danger Zone */}
      <div className={`${dynastyTheme.components.card.base} p-6 border-2 border-red-500/40`}>
        <div className="flex items-center space-x-3 mb-4">
          <AlertTriangle className={`w-6 h-6 ${dynastyTheme.classes.text.error}`} />
          <h3 className={`text-xl font-bold ${dynastyTheme.classes.text.white}`}>Danger Zone</h3>
        </div>
        
        <div className={`p-4 rounded-lg mb-4 bg-red-500/10 border border-red-500/20`}>
          <h4 className={`text-lg font-semibold ${dynastyTheme.classes.text.white} mb-2`}>Delete League</h4>
          <p className={`text-sm mb-4 ${dynastyTheme.classes.text.neutralLight}`}>
            Permanently delete this league and all associated data. This action cannot be undone.
          </p>
          
          <button
            onClick={() => setShowDeleteModal(true)}
            className={dynastyTheme.utils.getComponent('button', 'danger', 'sm')}
          >
            <Trash2 className="inline w-4 h-4 mr-2" />
            Delete League
          </button>
        </div>
      </div>

      {/* Delete Modal */}
      <DeleteLeagueModal
        isOpen={showDeleteModal}
        step={deleteStep}
        setStep={setDeleteStep}
        confirmText={deleteConfirmText}
        setConfirmText={setDeleteConfirmText}
        finalChecked={finalConfirmation}
        setFinalChecked={setFinalConfirmation}
        leagueName={league?.league_name}
        saving={saving}
        onConfirmDelete={handleDeleteLeague}
        onClose={() => setShowDeleteModal(false)}
      />
    </div>
  );
};

export default LeagueSettings;
